import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Button,
  Collapse,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  Build as BuildIcon
} from "@mui/icons-material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import DNSBinding from "../../components/DNSBinding/DNSbinding";
import TerraformConfig from "../../components/TerraformConfig/TerraformConfig";
import RedirectURIConfig from "../../components/RedirectUriConfig/RedirectUriConfig";
import { useParams, useSearchParams } from "react-router-dom";
import config from "../../env.json";
import { configureSuperset } from "../../apis/postProvisionApi/postProvisionApi";

type DeploymentStep = {
  Process_Id: number;
  Process_Name: string;
  status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED";
  Clone_Id?: number | null;
};

const ConfigureTenant = () => {
  const navigate = useNavigate();
  const { id: clientId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const clientName = searchParams.get("name");

  const [steps, setSteps] = useState<DeploymentStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);

  const [refreshKey, setRefreshKey] = useState(0);

  // Modal states
  const [openDNSModal, setOpenDNSModal] = useState(false);
  const [openTerraformModal, setOpenTerraformModal] = useState(false);
  const [openRedirectURIModal, setOpenRedirectURIModal] = useState(false);
  const [openSupersetModal, setOpenSupersetModal] = useState(false);

  // Fetch steps
  const fetchSteps = useCallback(async () => {
    try {
      const res = await axios.get(
        `${config.REACT_APP_ENV_API_BASE}/tenant/${clientId}/instance-status`
      );

      const apiSteps: DeploymentStep[] = res.data || [];

      // Inject Superset step if missing
      const hasSuperset = apiSteps.some((s) =>
        s.Process_Name.toLowerCase().includes("superset")
      );

      let augmented: DeploymentStep[] = hasSuperset
        ? apiSteps
        : [
            ...apiSteps,
            {
              Process_Id: 99999,
              Process_Name: "Superset",
              status: "NOT_STARTED"
            }
          ];

      //  Hide User Mapping step temporarily
      augmented = augmented.filter(
        (s) => !s.Process_Name.toLowerCase().includes("user mapping")
      );

      setSteps(augmented);

      const firstIncomplete = augmented.findIndex(
        (s: DeploymentStep) => s.status !== "COMPLETED"
      );
      setActiveStep(firstIncomplete !== -1 ? firstIncomplete : 0);
    } catch (err) {
      console.error("Error fetching steps", err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (clientId) fetchSteps();
  }, [clientId, refreshKey, fetchSteps]);

  const handleConfigComplete = () => {
    setRefreshKey((prev) => prev + 1);

    setOpenDNSModal(false);
    setOpenRedirectURIModal(false);
    setOpenTerraformModal(false);
    setOpenSupersetModal(false);
  };

  const handleConfigure = (stepIndex: number) => {
    const step = steps[stepIndex].Process_Name.toLowerCase();

    if (step.includes("domain binding")) setOpenDNSModal(true);
    else if (step.includes("terrform")) setOpenTerraformModal(true);
    else if (step.includes("redirect uri")) setOpenRedirectURIModal(true);
    else if (step.includes("user mapping")) return; // Disabled & hidden step
    else if (step.includes("superset")) setOpenSupersetModal(true);
    else if (step.includes("database cloning")) navigate(`/DatabaseConfigurations/comparisons/${steps[stepIndex].Clone_Id}`);
    else alert(`Configuring: ${steps[stepIndex].Process_Name}`);
  };

  const getStepColor = (status: DeploymentStep["status"]) => {
    switch (status) {
      case "COMPLETED":
        return "success.main";
      case "IN_PROGRESS":
        return "warning.main";
      default:
        return "grey.300";
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  const ConfigDialog = ({ open, onClose, title, children }: any) => (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          bgcolor: "#734BD1",
          color: "white",
          fontWeight: "bold"
        }}
      >
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "white" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>{children}</DialogContent>
    </Dialog>
  );

  const SupersetDialog = ({ onComplete }: { onComplete: () => void }) => {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const markComplete = async () => {
      setSaving(true);
      setError(null);

      try {
        await configureSuperset({
          id: clientId!,
          name: clientName ?? ""
        });

        setSteps((prev) =>
          prev.map((s) =>
            s.Process_Name.toLowerCase() === "superset"
              ? { ...s, status: "COMPLETED" as const }
              : s
          )
        );
        onComplete();
        setOpenSupersetModal(false);
      } catch (err) {
        console.error("Error configuring Superset:", err);
        setError("Failed to configure Superset. Please try again.");
      } finally {
        setSaving(false);
      }
    };

    return (
      <Box>
        <Typography variant="body1" gutterBottom>
          Complete Superset configuration.
        </Typography>
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            startIcon={<AssessmentIcon />}
            onClick={markComplete}
            disabled={saving}
          >
            {saving ? "Configuring..." : "Initiate Superset"}
          </Button>
          <Button variant="outlined" onClick={() => setOpenSupersetModal(false)}>
            Close
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Box p={3}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Post Provisioning Steps – {clientName} #{clientId}
        </Typography>

        <Stepper activeStep={activeStep} orientation="vertical" nonLinear>
          {steps.map((step, index) => (
            <Step
              key={`${step.Process_Id}-${step.Process_Name}`}
              onClick={() => setActiveStep(index)}
            >
              <StepLabel
                icon={
                  step.status === "COMPLETED" ? (
                    <CheckCircleIcon color="success" />
                  ) : step.status === "IN_PROGRESS" ? (
                    <HourglassEmptyIcon color="warning" />
                  ) : step.Process_Name.toLowerCase().includes("superset") ? (
                    <AssessmentIcon color="disabled" />
                  ) : (
                    <SettingsIcon color="disabled" />
                  )
                }
              >
                <Box display="flex" alignItems="center">
                  <Typography
                    variant="h6"
                    fontWeight={activeStep === index ? "bold" : "normal"}
                    color={activeStep === index ? "#734BD1" : "inherit"}
                  >
                    {step.Process_Name}
                  </Typography>
                  <Chip
                    label={step.status.replace("_", " ")}
                    color={
                      step.status === "COMPLETED"
                        ? "success"
                        : step.status === "IN_PROGRESS"
                        ? "warning"
                        : "default"
                    }
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Box>
              </StepLabel>

              <Collapse in={activeStep === index}>
                <Card
                  variant="outlined"
                  sx={{
                    mt: 1,
                    mb: 2,
                    ml: 4,
                    bgcolor: activeStep === index ? "grey.50" : "transparent",
                    borderLeft: `4px solid ${getStepColor(step.status)}`
                  }}
                >
                  <CardContent>
                    <Typography variant="body2" gutterBottom>
                      Configure <b>{step.Process_Name}</b> to complete this
                      stage.
                    </Typography>

                    {!step.Process_Name.toLowerCase().includes("terrform") && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<BuildIcon />}
                        color={
                          step.status === "IN_PROGRESS"
                            ? "warning"
                            : step.status === "COMPLETED"
                            ? "success"
                            : "primary"
                        }
                        onClick={() => handleConfigure(index)}
                      >
                        Configure
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Collapse>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Modals */}
      <ConfigDialog
        open={openDNSModal}
        onClose={() => setOpenDNSModal(false)}
        title="Domain Binding Configuration"
      >
        <DNSBinding
          clientId={clientId}
          clientName={clientName ?? ""}
          onComplete={handleConfigComplete}
        />
      </ConfigDialog>

      <ConfigDialog
        open={openTerraformModal}
        onClose={() => setOpenTerraformModal(false)}
        title="Terraform Configuration"
      >
        <TerraformConfig />
      </ConfigDialog>

      <ConfigDialog
        open={openRedirectURIModal}
        onClose={() => setOpenRedirectURIModal(false)}
        title="Redirect URI Configuration"
      >
        <RedirectURIConfig
          onComplete={handleConfigComplete}
          clientId={clientId}
        />
      </ConfigDialog>

      <ConfigDialog
        open={openSupersetModal}
        onClose={() => setOpenSupersetModal(false)}
        title="Superset Configuration"
      >
        <SupersetDialog onComplete={handleConfigComplete} />
      </ConfigDialog>
    </>
  );
};

export default ConfigureTenant;
